/**
 * The unified select-mode inspector that Electron MAIN injects into
 * EVERY cross-origin preview iframe (TV + each controller) via
 * `webFrameMain.executeJavaScript` (select-to-chat,
 * `docs/design/preview-select-to-chat-prd.md`).
 *
 * Runs in the IFRAME's world, so fully self-contained — it cannot
 * import any crucible-ui module at runtime (descriptor extraction +
 * drill logic are inlined). The `__crucibleInspect` message shapes
 * mirror `inspect-protocol.ts`; the descriptor caps come from
 * `INSPECT_DESCRIPTOR_*` in `crucible-ipc-types` via `cfg`.
 *
 * On `arm` (the user paused the preview) this screen:
 *   - pauses CSS animations/transitions (soft visual freeze) and shows
 *     a transparent capture overlay so the running game receives no
 *     pointer input while selecting;
 *   - HOVER highlights the closest element under the cursor;
 *   - CLICK locks the selection on that element; a further click /
 *     double-click drills one level DEEPER (a child still under the
 *     point); Esc steps back out;
 *   - DRAG (past a small threshold) draws an area marquee instead;
 *   - a floating "Add to chat" button at the selection's corner posts
 *     the selection (`{kind, rect, viewport, descriptor?}`) to the
 *     parent, which captures the pixels and routes it to chat.
 *
 * `disarm` (resume) tears all of that down and unpauses.
 *
 * Comms: posts to the parent (Foundry renderer) via
 * `window.parent.postMessage`, targeting the parent origin captured
 * from the first inbound command (`ready` goes to `'*'` first — it
 * carries no data). The renderer validates source+origin.
 */

export interface InspectorScriptConfig {
  marker: string;
  textCap: number;
  maxClasses: number;
  /** Drag distance (px) past which a gesture is an area marquee rather
   *  than an element click. */
  dragThreshold: number;
}

function inspectorMain(cfg: {
  marker: string;
  textCap: number;
  maxClasses: number;
  dragThreshold: number;
}): void {
  const MARK = cfg.marker;
  const w = window as unknown as Record<string, unknown>;
  const prevTeardown = w.__crucibleInspectorTeardown;
  if (typeof prevTeardown === 'function') {
    try {
      (prevTeardown as () => void)();
    } catch {
      /* ignore */
    }
  }

  let armed = false;
  let parentOrigin = '*';

  // Elements covering ≥ this fraction of the viewport are treated as
  // screen-filling wrappers (root / full-bleed containers) and skipped
  // as a hover START — so hovering never highlights the whole TV/phone.
  const SCREEN_FILL_RATIO = 0.88;

  // Cap on the visible-text snapshot sent with a selection (Option A:
  // gives the agent the whole screen's text so it can tell WHICH scene /
  // state the selection came from and grep the matching manifest).
  const SCREEN_TEXT_CAP = 1200;

  // Interaction state. `stack` is the navigable hit stack with
  // index 0 = the first "real" (non-screen-filling) block under the
  // cursor and increasing index = deeper/more-specific. So depth+1
  // (click again / double-click) drills DEEPER, depth-1 (Esc) steps to
  // the PARENT. Frozen while a selection is locked.
  let stack: Element[] = [];
  let depth = 0;
  let lockedEl: Element | null = null;
  let areaRect: { x: number; y: number; width: number; height: number } | null = null;
  let dragStart: { x: number; y: number } | null = null;
  let dragging = false;

  const ACCENT = '#2145f7';

  const style = document.createElement('style');
  style.setAttribute('data-crucible-inspector', 'style');
  style.textContent =
    '*,*::before,*::after{animation-play-state:paused !important;transition-duration:0s !important;}';

  const overlay = document.createElement('div');
  overlay.setAttribute('data-crucible-inspector', 'overlay');
  // Focusable so a click can move keyboard focus INTO this iframe —
  // we preventDefault on mousedown (to block game input), which would
  // otherwise suppress the default focus shift, leaving Esc with the
  // parent and making it exit select mode instead of drilling out.
  overlay.tabIndex = -1;
  overlay.style.outline = 'none';
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '2147483645',
    cursor: 'crosshair',
    background: 'transparent',
    display: 'none',
  } as CSSStyleDeclaration);

  const box = document.createElement('div');
  box.setAttribute('data-crucible-inspector', 'box');
  Object.assign(box.style, {
    position: 'fixed',
    pointerEvents: 'none',
    zIndex: '2147483646',
    border: '2px solid ' + ACCENT,
    background: 'rgba(33,69,247,0.12)',
    borderRadius: '2px',
    display: 'none',
    boxSizing: 'border-box',
  } as CSSStyleDeclaration);

  const addBtn = document.createElement('button');
  addBtn.setAttribute('data-crucible-inspector', 'add');
  addBtn.type = 'button';
  addBtn.textContent = 'Add to Chat';
  Object.assign(addBtn.style, {
    position: 'fixed',
    zIndex: '2147483647',
    display: 'none',
    padding: '5px 10px',
    font: '500 12px ui-sans-serif,system-ui,-apple-system,sans-serif',
    color: '#fff',
    background: ACCENT,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
  } as CSSStyleDeclaration);

  function post(msg: Record<string, unknown>): void {
    try {
      window.parent.postMessage(msg, parentOrigin);
    } catch {
      /* drop */
    }
  }

  function clampDepth(d: number, len: number): number {
    if (len <= 0) return 0;
    if (!Number.isFinite(d) || d < 0) return 0;
    const max = len - 1;
    return d > max ? max : Math.floor(d);
  }

  function describe(el: Element): Record<string, unknown> {
    const tag = el.tagName.toLowerCase();
    const collapsed = (el.textContent || '').replace(/\s+/g, ' ').trim();
    const text =
      collapsed.length > cfg.textCap ? collapsed.slice(0, cfg.textCap - 1) + '…' : collapsed;
    const classes = (el.getAttribute('class') || '')
      .split(/\s+/)
      .map((c) => c.trim())
      .filter((c) => c.length > 0)
      .slice(0, cfg.maxClasses);
    const d: Record<string, unknown> = { tag, text, classes };
    const role = el.getAttribute('role');
    if (role) d.role = role;
    const aria = el.getAttribute('aria-label');
    if (aria) d.ariaLabel = aria;
    if (tag === 'img') {
      const src = el.getAttribute('src');
      if (src) d.src = src;
    } else if (tag === 'canvas') {
      d.src = '(canvas — rendered pixels, no inner DOM)';
    }
    return d;
  }

  function computeStack(x: number, y: number): Element[] {
    const raw = document.elementsFromPoint(x, y); // innermost-first
    const chain = raw
      .filter((el) => {
        if (el.closest('[data-crucible-inspector]')) return false;
        const t = el.tagName;
        return t !== 'HTML' && t !== 'BODY';
      })
      .reverse(); // outermost-first: [root-ish, …, leaf]
    // Drop leading screen-filling wrappers so index 0 is the first
    // block meaningfully smaller than the screen. Increasing index =
    // deeper toward the leaf.
    const screenArea = window.innerWidth * window.innerHeight;
    let start = 0;
    while (start < chain.length - 1) {
      const r = chain[start].getBoundingClientRect();
      if (r.width * r.height < SCREEN_FILL_RATIO * screenArea) break;
      start++;
    }
    return chain.slice(start);
  }

  function rectOf(el: Element): { x: number; y: number; width: number; height: number } {
    const r = el.getBoundingClientRect();
    return { x: r.left, y: r.top, width: r.width, height: r.height };
  }

  function paintBox(r: { x: number; y: number; width: number; height: number }): void {
    box.style.display = 'block';
    box.style.left = r.x + 'px';
    box.style.top = r.y + 'px';
    box.style.width = r.width + 'px';
    box.style.height = r.height + 'px';
  }

  function placeAddBtn(r: { x: number; y: number; width: number; height: number }): void {
    addBtn.style.display = 'block';
    // Bottom-right corner, nudged inside the viewport.
    const bw = addBtn.offsetWidth || 90;
    const bh = addBtn.offsetHeight || 28;
    let left = r.x + r.width - bw;
    let top = r.y + r.height + 6;
    if (top + bh > window.innerHeight) top = Math.max(4, r.y - bh - 6);
    if (left < 4) left = 4;
    if (left + bw > window.innerWidth - 4) left = window.innerWidth - 4 - bw;
    addBtn.style.left = left + 'px';
    addBtn.style.top = top + 'px';
  }

  function currentSelectionRect(): { x: number; y: number; width: number; height: number } | null {
    if (areaRect) return areaRect;
    if (lockedEl) return rectOf(lockedEl);
    return null;
  }

  function showSelectionAffordance(): void {
    const r = currentSelectionRect();
    if (!r) {
      addBtn.style.display = 'none';
      return;
    }
    paintBox(r);
    placeAddBtn(r);
  }

  function clearSelection(): void {
    lockedEl = null;
    areaRect = null;
    stack = [];
    depth = 0;
    box.style.display = 'none';
    addBtn.style.display = 'none';
  }

  function onMove(e: MouseEvent): void {
    if (!armed) return;
    if (dragStart && (e.buttons & 1) === 1) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      if (dragging || Math.abs(dx) >= cfg.dragThreshold || Math.abs(dy) >= cfg.dragThreshold) {
        dragging = true;
        lockedEl = null;
        const r = {
          x: Math.min(dragStart.x, e.clientX),
          y: Math.min(dragStart.y, e.clientY),
          width: Math.abs(dx),
          height: Math.abs(dy),
        };
        areaRect = r;
        paintBox(r);
        addBtn.style.display = 'none';
      }
      return;
    }
    // Plain hover — only while nothing is committed.
    if (lockedEl || areaRect) return;
    stack = computeStack(e.clientX, e.clientY);
    if (stack.length === 0) {
      box.style.display = 'none';
      return;
    }
    depth = 0;
    paintBox(rectOf(stack[depth]));
  }

  function onDown(e: MouseEvent): void {
    if (!armed) return;
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    // preventDefault suppresses the click's default focus shift, so
    // focus the overlay explicitly — this routes Esc to THIS iframe's
    // inspector (drill out / clear), not the parent's exit handler.
    try {
      overlay.focus({ preventScroll: true });
    } catch {
      /* ignore */
    }
    dragStart = { x: e.clientX, y: e.clientY };
    dragging = false;
  }

  function onUp(e: MouseEvent): void {
    if (!armed) return;
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const wasDragging = dragging;
    const start = dragStart;
    dragStart = null;
    dragging = false;
    if (wasDragging && areaRect && areaRect.width > 2 && areaRect.height > 2) {
      // Area committed; affordance already painted.
      placeAddBtn(areaRect);
      return;
    }
    if (!start) return;
    // Click (no drag): first click locks the hovered block; clicking
    // again (or double-click) drills DEEPER toward the leaf.
    if (!lockedEl) {
      if (stack.length === 0) {
        stack = computeStack(e.clientX, e.clientY);
        depth = 0;
      }
      if (stack.length === 0) return;
      lockedEl = stack[depth];
    } else {
      depth = clampDepth(depth + 1, stack.length);
      lockedEl = stack[depth] || lockedEl;
    }
    showSelectionAffordance();
  }

  function onKey(e: KeyboardEvent): void {
    if (!armed) return;
    if (e.key === 'Escape') {
      // Esc ladder: drill out → clear selection → leave select mode.
      if (lockedEl && depth > 0) {
        // Element drill: step OUT to the parent layer.
        e.preventDefault();
        depth = clampDepth(depth - 1, stack.length);
        lockedEl = stack[depth] || null;
        showSelectionAffordance();
      } else if (lockedEl || areaRect) {
        // A selection is framed: dismiss it (back to hovering).
        e.preventDefault();
        clearSelection();
      } else {
        // Nothing selected: ask the parent to exit select mode. (Works
        // from a focused iframe, where the parent never sees the Esc.)
        e.preventDefault();
        post({ [MARK]: 'exit' });
      }
    }
  }

  function onLeave(): void {
    // Cursor left this screen — clear the hover highlight so it doesn't
    // linger (e.g. moving from the TV onto a phone). A committed
    // selection (locked element / framed area) stays put.
    if (!armed || lockedEl || areaRect || dragging) return;
    stack = [];
    box.style.display = 'none';
  }

  // Visible text of the whole screen (Option A). `innerText` is
  // visible-only and in reading order; normalise whitespace + cap.
  function collectScreenText(): string {
    const raw = (document.body && (document.body as HTMLElement).innerText) || '';
    const text = raw
      .replace(/[ \t]+/g, ' ')
      .replace(/\n{2,}/g, '\n')
      .trim();
    return text.length > SCREEN_TEXT_CAP ? text.slice(0, SCREEN_TEXT_CAP - 1) + '…' : text;
  }

  function onAddClick(e: MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
    const r = currentSelectionRect();
    if (!r) return;
    const msg: Record<string, unknown> = {
      [MARK]: 'selection',
      kind: areaRect ? 'area' : 'element',
      rect: r,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      screenText: collectScreenText(),
    };
    if (!areaRect && lockedEl) msg.descriptor = describe(lockedEl);
    post(msg);
    clearSelection();
  }

  function arm(): void {
    if (armed) return;
    armed = true;
    clearSelection();
    document.head.appendChild(style);
    overlay.style.display = 'block';
    overlay.addEventListener('mousemove', onMove, true);
    overlay.addEventListener('mousedown', onDown, true);
    overlay.addEventListener('mouseup', onUp, true);
    overlay.addEventListener('mouseleave', onLeave, true);
    window.addEventListener('blur', onLeave);
    window.addEventListener('keydown', onKey, true);
  }

  function disarm(): void {
    armed = false;
    clearSelection();
    overlay.style.display = 'none';
    overlay.removeEventListener('mousemove', onMove, true);
    overlay.removeEventListener('mousedown', onDown, true);
    overlay.removeEventListener('mouseup', onUp, true);
    overlay.removeEventListener('mouseleave', onLeave, true);
    window.removeEventListener('blur', onLeave);
    window.removeEventListener('keydown', onKey, true);
    if (style.parentNode) style.parentNode.removeChild(style);
  }

  function onMessage(e: MessageEvent): void {
    const data = e.data;
    if (!data || typeof data !== 'object') return;
    const kind = (data as Record<string, unknown>)[MARK];
    if (typeof kind !== 'string') return;
    if (e.origin && e.origin !== 'null') parentOrigin = e.origin;
    if (kind === 'arm') arm();
    else if (kind === 'disarm') disarm();
  }

  function teardown(): void {
    disarm();
    window.removeEventListener('message', onMessage);
    overlay.remove();
    box.remove();
    addBtn.remove();
    delete w.__crucibleInspectorTeardown;
  }

  addBtn.addEventListener('click', onAddClick, true);
  document.body.appendChild(overlay);
  document.body.appendChild(box);
  document.body.appendChild(addBtn);
  window.addEventListener('message', onMessage);
  w.__crucibleInspectorTeardown = teardown;

  post({ [MARK]: 'ready' });
}

/**
 * Serialise `inspectorMain` into an injectable IIFE string. Main calls
 * `webFrameMain.executeJavaScript(buildInspectorScript(cfg))`.
 */
export function buildInspectorScript(cfg: InspectorScriptConfig): string {
  return `(${inspectorMain.toString()})(${JSON.stringify(cfg)});`;
}
